import React, { useState } from 'react';
import { Modal } from '@ofself/shell';
import { Facts, Note } from '../../components/nucleus/Inspector';
import { Row } from '../../components/nucleus/Collection';
import { SheetFoot, FootButton } from '../../components/nucleus/SheetFoot';
import { ErrorEmpty } from '../../components/nucleus/Empty';
import StatusPill from '../../components/nucleus/StatusPill';
import { useConfirm } from '../../components/nucleus/actions';
import { usePlace } from '../../nucleus/place';
import { useSpaceAccess } from '../../nucleus/access';
import { requestsApi } from './data/api';
import { usePluginRequest, useSetupConfig } from './data/hooks';
import { requestStateLine, expiresWord } from './data/toolruns';
import './PluginsSection.css';
import './tool-sheet.css';

// A PARKED RUN, WAITING FOR YOU (plugin lifecycle §3.7): one run an app, an
// agent or a person asked for, which stopped for a yes. Who asked, which tool,
// why it asked, its inputs (by label), the realm it would run in, what it
// would touch and do outside ofSelf, and how long it waits. Allow (asked
// once more) or Decline (a danger, asked first). Answered, expired or
// cancelled is said in a line, with nothing to press.
//
//   <RequestSheet groupId requestId onClose onAnswered />
// The /grant?request_id= page draws the same parts (RequestView) on its own frame.

const capital = (s) => (s ? `${s[0].toUpperCase()}${s.slice(1)}` : s);
const valueWord = (v) => {
    if (v == null || v === '') return '—';
    if (typeof v === 'boolean') return v ? 'On' : 'Off';
    if (Array.isArray(v)) return v.map(valueWord).join(', ');
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
};

/** The request's inputs, each by its field's label where the tool says one. */
function inputRows(input, fields) {
    const labelOf = (k) => (fields || []).find((f) => f.key === k)?.label || capital(String(k).replace(/_/g, ' '));
    return Object.entries(input || {}).map(([k, v]) => ({ key: k, label: labelOf(k), value: valueWord(v) }));
}

/** Allow or Decline one parked run: { answer(kind), busy, error, answered }. */
export function useAnswer(request, groupId, onAnswered) {
    const confirm = useConfirm();
    const [busy, setBusy] = useState(null);
    const [error, setError] = useState(null);
    const [answered, setAnswered] = useState(null); // { status, runId }
    const answer = async (kind) => {
        const allow = kind === 'allow';
        const ok = await confirm(allow
            ? {
                title: `Allow ${request.toolName || 'this run'}?`,
                body: `It runs once${request.realm?.name ? `, in ${request.realm.name}` : ''}.`,
                label: 'Allow', danger: false,
            }
            : { title: `Decline ${request.toolName || 'this run'}?`, body: 'It won’t run.', label: 'Decline', danger: true });
        if (!ok) return;
        setBusy(kind); setError(null);
        try {
            const res = allow
                ? await requestsApi.approve(request.pluginId, request.id, groupId)
                : await requestsApi.deny(request.pluginId, request.id, groupId);
            const done = {
                status: allow ? 'executed' : 'denied',
                runId: res?.execution?.run_id || res?.run_id || null,
            };
            setAnswered(done);
            onAnswered?.(done);
        } catch (e) {
            // the request moved on while it was open: say where it stands
            if (e?.status === 410) setAnswered({ status: 'expired', runId: null });
            else setError(e?.message || (allow ? 'Could not allow it.' : 'Could not decline it.'));
        } finally {
            setBusy(null);
        }
    };
    return { answer, busy, error, answered };
}

/** The request, read: its facts, inputs, what it would touch and do. */
export function RequestFacts({ request, fields, onOpenNode }) {
    const r = request;
    const inputs = inputRows(r.input, fields);
    const effects = Array.isArray(r.effects) ? r.effects : [];
    const nodes = Array.isArray(r.affectedNodes) ? r.affectedNodes : [];
    return (
        <>
            <Facts items={[
                { label: 'Asked by', value: r.askedBy },
                { label: 'Tool', value: r.toolName || r.pluginName },
                { label: 'Runs in', value: r.realm?.name || '—' },
                r.status === 'pending' && r.expires && { label: 'Waits', value: expiresWord(r.expires) },
            ]} />
            <div className="plugins-rq__part">
                <span className="plugins-label">Why it asks</span>
                <p className="plugins-rq__why">{r.why}</p>
            </div>
            {inputs.length > 0 && (
                <div className="plugins-rq__part">
                    <span className="plugins-label">Inputs</span>
                    {inputs.map((x) => <Row key={x.key} name={x.label} sub={x.value} />)}
                </div>
            )}
            {effects.length > 0 && (
                <div className="plugins-rq__part">
                    <span className="plugins-label">Outside ofSelf</span>
                    {effects.map((e, i) => (
                        <Row key={i} name={capital(e.description || 'Something outside ofSelf')} end={e.reversible === false ? 'Can’t be undone' : undefined} endTone="warn" />
                    ))}
                </div>
            )}
            {(nodes.length > 0 || r.affected > 0) && (
                <div className="plugins-rq__part">
                    <span className="plugins-label">It would touch</span>
                    {nodes.map((n) => (
                        <Row key={n.id} name={n.title || 'Untitled'} sub={n.schema_name || undefined} onPress={onOpenNode ? () => onOpenNode(n.id) : undefined} />
                    ))}
                    {!nodes.length && <Note>{`${r.affected} ${r.affected === 1 ? 'node' : 'nodes'} you can’t see from here.`}</Note>}
                </div>
            )}
            {r.removeSource && <Note tone="warn">It deletes what it reads once it is done.</Note>}
        </>
    );
}

/** The whole request with its answer: the facts, then Allow / Decline, or the line that says where it stands. */
export function RequestView({ request, groupId, fields, canAllow = true, allowHint, onOpenNode, onOpenRun, onAnswered }) {
    const { answer, busy, error, answered } = useAnswer(request, groupId, onAnswered);
    const status = answered?.status || request.status;
    const settled = requestStateLine(status);
    const runId = answered?.runId || request.runId;
    const heldId = `rq-held-${request.id}`;
    return (
        <div className="plugins-rq">
            <p className="plugins-rq__lead">
                <b>{request.askedBy}</b>{status === 'pending' ? ' wants to run ' : ' asked to run '}<b>{request.toolName || 'a tool'}</b>{` of ${request.pluginName}.`}
            </p>
            <RequestFacts request={request} fields={fields} onOpenNode={onOpenNode} />
            <div aria-live="polite" className="plugins-rq__said">
                {settled && (
                    <p className="plugins-rq__settled">
                        <StatusPill status={status === 'executed' || status === 'approved' ? 'done' : status === 'denied' ? 'refused' : 'skipped'} of="run">
                            {status === 'executed' || status === 'approved' ? 'Allowed' : status === 'denied' ? 'Declined' : capital(status)}
                        </StatusPill>
                        <span>{settled}</span>
                        {runId && onOpenRun && <button type="button" className="nx-link" onClick={() => onOpenRun(runId)}>Open the run</button>}
                    </p>
                )}
                {error && <p className="plugins-error" role="alert">{error}</p>}
            </div>
            {!settled && (
                <>
                    <SheetFoot className="plugins-rq__foot" left={<FootButton danger busy={busy === 'deny'} disabled={!!busy} onClick={() => answer('deny')}>Decline</FootButton>}>
                        <FootButton primary busy={busy === 'allow'} disabled={!!busy || !canAllow} onClick={() => answer('allow')} aria-describedby={!canAllow ? heldId : undefined}>
                            Allow
                        </FootButton>
                    </SheetFoot>
                    {!canAllow && allowHint && <p id={heldId} className="plugins-ts__held">{allowHint}</p>}
                </>
            )}
        </div>
    );
}

export default function RequestSheet({ groupId = null, requestId, onClose, onAnswered }) {
    const { to, navigate } = usePlace();
    const access = useSpaceAccess();
    const read = usePluginRequest(groupId, requestId);
    const r = read.data;
    const setup = useSetupConfig(groupId, r?.pluginId, r?.toolId || null, { enabled: !!r?.pluginId });
    const fields = (setup.data?.fields || []).filter((f) => !f.secret);
    // in a group some runs only an admin may allow; any member may decline
    const canAllow = !r?.adminOnly || access.isAdmin;
    const allowHint = canAllow ? null : `${access.onlyAdmin?.('allow this run') || 'Only an admin can allow this run'}.`;
    const go = (path) => { onClose?.(); navigate(path); };
    // not there, or not one this viewer may see, is said plainly; any other failure offers Try again
    const gone = !r && !read.loading && ((read.error && [403, 404].includes(read.error.status)) || (!read.error && read.data === null));
    const failed = !r && !!read.error && !gone;
    return (
        <Modal open onClose={onClose} title={!r || r.status === 'pending' ? 'Waiting for you' : 'Asked to run'} size="md" className="plugins-rq-modal">
            {failed && <ErrorEmpty what="this request" error={read.error} retry={read.reload} />}
            {gone && <Note tone="live">That request isn’t here: it may have been removed, or it isn’t one you can see.</Note>}
            {r && !r.pluginId && <p className="plugins-rq__settled">{requestStateLine(r.status)}</p>}
            {r && r.pluginId && (
                <RequestView
                    request={r} groupId={groupId} fields={fields} canAllow={canAllow} allowHint={allowHint}
                    onAnswered={onAnswered}
                    onOpenNode={(id) => go(to('graph', null, `open=${encodeURIComponent(id)}`))}
                    onOpenRun={(id) => go(to('plugins', 'runs', `open=${encodeURIComponent(id)}`))}
                />
            )}
        </Modal>
    );
}
